import { useDialog } from '@globals/dialog/useDialogStore'
import { colors } from '@globals/themes/defaultTheme'
import { AwardType, listAwardRecipients } from '@kudos/http-client'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Cancel from '@shared/forms/Cancel'
import ErrorText from '@shared/forms/ErrorText'
import FieldLabel from '@shared/forms/FieldLabel'
import FormContextWrapper from '@shared/forms/FormContextWrapper'
import Submit from '@shared/forms/Submit'
import TextField from '@shared/forms/TextField'
import MentionsTextareaField from '@shared/forms/mentionsTextareaField/MentionsTextareaField'
import TenantMembersAndTeamsSearchField from '@shared/forms/tenantMemberAndTeamSearchField/TenantMemberAndTeamSearchField'
import useRecipientsPreview from '@shared/hooks/useRecipientsPreview'
import { MAX_MESSAGE_CHARS } from '@shared/wallPost/constants'
import { T, useT } from '@transifex/react'
import { isNull } from 'lodash'
import { useEffect } from 'react'
import { useWatch } from 'react-hook-form'
import { NumericFormat } from 'react-number-format'
import { useLocation } from 'react-router-dom'
import AwardsPointsRecipientAmountCard from './AwardsPointsRecipientAmountCard'
import { SEND_AWARDS_MODAL_KEY } from './SendAwardsModal'
import useSendAwardForm, { SendAwardFormData } from './useSendAwardForm'
import { useSendAwardFormStore } from './useSendAwardFormStore'
import EmojiPlugin from '@components/mentionsTextarea/EmojiPlugin'

type SendAwardFormProps = {
  awardType: AwardType
}
const SendAwardForm: React.FC<SendAwardFormProps> = ({ awardType }) => {
  const location = useLocation()
  const t = useT()
  const { closeDialog } = useDialog(SEND_AWARDS_MODAL_KEY)
  const { setIsFormDirty } = useSendAwardFormStore()
  const { formMethods, publishAward, handleResetAwardForm, isFormDirty } =
    useSendAwardForm(awardType)

  const { control, setValue } = formMethods
  const amount = useWatch({ control, name: 'amount' })
  const { recipientCount, pointsRecipientCount, totalSpend, pointsBankBalance } =
    useRecipientsPreview(control, awardType.id)

  useEffect(() => {
    if (location.state) {
      const recipients = location.state.recipients
      if (recipients && recipients.length > 0) {
        formMethods.setValue('sendTo', recipients, { shouldValidate: true, shouldDirty: true })
      }
    }
  }, [location])

  useEffect(() => {
    setIsFormDirty(isFormDirty)
  }, [isFormDirty])

  const onSubmit = (data: SendAwardFormData) => {
    publishAward(data, () => {
      handleResetAwardForm()
      closeDialog()
    })
  }
  return (
    <FormContextWrapper
      onSubmit={onSubmit}
      formMethods={formMethods}
      formActions={
        <>
          <Cancel onClick={() => closeDialog()} />
          <Submit formState={formMethods.formState} submitText={<T _str="Send" />} />
        </>
      }>
      <Stack mt={2}>
        <Typography mb={2} variant="body1">
          {awardType.description}
        </Typography>
        <TenantMembersAndTeamsSearchField
          control={control}
          name="sendTo"
          label={<T _str="To" />}
          searchFn={listAwardRecipients}
          showAvatars
          zIndexPlacement="modal"
        />
        {recipientCount > pointsRecipientCount && awardType.points > 0 && amount > 0 && (
          <Stack useFlexGap direction="row" mt={2} spacing={2}>
            <InfoOutlinedIcon sx={{ color: colors.bluePrimary }} />
            <Typography>
              <T
                _str="{ineligible} {individuals} won't receive points with their award because of your organization's eligibility rules"
                ineligible={<b>{recipientCount - pointsRecipientCount}</b>}
                individuals={
                  <b>
                    <T
                      _str="{ineligible, plural, one {individual} other {individuals}}"
                      ineligible={recipientCount - pointsRecipientCount}
                    />
                  </b>
                }
              />
            </Typography>
          </Stack>
        )}
        <Box>
          <MentionsTextareaField
            name="message"
            control={control}
            placeholder={
              <T _str="Share a few words about their achievement and why they’ve earned this award" />
            }
            maxChars={MAX_MESSAGE_CHARS}
            ariaLabel={t('Write a message')}
            label={<T _str="Message" />}
            isMentionsSupported>
            <EmojiPlugin id="emojiSendAward" />
          </MentionsTextareaField>
        </Box>
        {!awardType.pointsLocked && (
          <Box mb={2} maxWidth={180}>
            <FieldLabel>
              <T _str="Points per recipient" />
            </FieldLabel>
            <NumericFormat
              name="amount"
              value={amount}
              customInput={TextField}
              onChange={event => {
                setValue('amount', parseInt(event.target.value, 10), { shouldValidate: true })
              }}
              isAllowed={input => Number(input.value) >= 0}
              allowNegative={false}
              decimalScale={0}
            />
          </Box>
        )}
        {/* Even though points amount is hidden we still need to show errors. */}
        {awardType.pointsLocked && formMethods.formState.errors.amount?.message && (
          <ErrorText>{formMethods.formState.errors.amount?.message}</ErrorText>
        )}

        {awardType && amount > 0 && !isNull(pointsBankBalance) && (
          <AwardsPointsRecipientAmountCard
            pointsAmount={amount}
            pointsRecipientCount={pointsRecipientCount}
            totalSpend={totalSpend}
            pointsBankAvailableBalance={pointsBankBalance.available}
          />
        )}
      </Stack>
    </FormContextWrapper>
  )
}

export default SendAwardForm
